/**
 * Pure, DOM-free builder for the pre-filled GitHub bug-report URL.
 *
 * Two callers need the SAME URL/body shape from two different runtimes:
 *
 *   - The Electron desktop handler (`crucible-gui/src/main/
 *     ipc-surfaces/app-open-bug-report.ts`) builds the URL, opens it with
 *     `shell.openExternal`, and writes the body to the system clipboard. It
 *     ALSO bundles a full diagnostics blob it collects from main.
 *   - The web / cloud build has no Electron main, so `CrucibleErrorState`
 *     (`@volley/crucible-ui`) calls this builder directly and opens the URL
 *     with `window.open` (issue #2179 — the desktop-only IPC channel rejects
 *     in the browser, leaving the "Create bug report" button dead).
 *
 * This module is the web path's builder. It lives in `crucible-ipc-types`
 * (the shared leaf package both `crucible-ui` and `crucible-gui` depend on)
 * so the surrogate-safe truncation + secret scrub aren't reimplemented per
 * surface. The desktop handler keeps its own composition because it
 * interleaves async diagnostics collection + Electron clipboard/open; the
 * web path has neither, so it only needs `errorJson`.
 */
import { redactFlaggedSecrets } from './diagnostics-secret-scan.js';
/** GitHub's practical body cap is ~8KB end-to-end after percent-encoding;
 *  cap on the ENCODED length with a conservative 7KB ceiling (the encoded
 *  form grows ~1.5-3× as newlines become `%0A` etc.). Matches the desktop
 *  handler's `ENCODED_CAP`. */
const ENCODED_CAP = 7 * 1024;
const ISSUE_URL_BASE = 'https://github.com/Volley-Inc/crucible/issues/new';
const DEFAULT_TITLE = 'Bug report from Foundry';
const TRUNCATION_MARKER = '\n\n_(diagnostics truncated to fit the URL — full version on the clipboard, paste below)_';
/**
 * Truncate `fullBody` so its `encodeURIComponent` form is at most
 * `encodedCap` characters. Returns the original body unchanged when it
 * already fits.
 *
 * Iterates over Unicode CODE POINTS (`Array.from`) rather than UTF-16 code
 * units: `String.slice` can split a surrogate pair, leaving a lone surrogate
 * that `encodeURIComponent` rejects with `URIError: URI malformed`. The
 * diagnostics blob + the user's error JSON can both carry non-BMP characters
 * (emoji, CJK extension B).
 *
 * NB: a deliberate sibling of `crucible-gui/src/main/bug-report-url.ts`'s
 * function of the same name (the desktop handler injects that one via DI).
 * Same algorithm, same marker — kept in sync by the matching unit tests on
 * both sides. If you change the algorithm here, change it there too.
 */
export function truncateForUrlEncoding(fullBody, encodedCap) {
    if (encodeURIComponent(fullBody).length <= encodedCap) {
        return { body: fullBody, truncated: false };
    }
    const codePoints = Array.from(fullBody);
    let cpLen = codePoints.length;
    while (cpLen > 0) {
        const candidate = codePoints.slice(0, cpLen).join('') + TRUNCATION_MARKER;
        if (encodeURIComponent(candidate).length <= encodedCap) {
            return { body: candidate, truncated: true };
        }
        cpLen -= 64;
    }
    // Cap too small for even the marker — emit just the trimmed marker.
    return { body: TRUNCATION_MARKER.trimStart(), truncated: true };
}
/**
 * Compose the markdown issue body from the renderer's structured error
 * JSON. Mirrors the desktop handler's template minus the `## Diagnostics`
 * section (the web build can't collect main-process diagnostics).
 */
export function composeBugReportBody(errorJson) {
    return [
        '## What happened',
        '',
        '_Describe what you were doing when the error appeared._',
        '',
        '## Error details',
        '',
        '```json',
        errorJson,
        '```',
    ].join('\n');
}
/**
 * Build the pre-filled GitHub bug-report URL + clipboard body for the web
 * path. The composed body is run through `redactFlaggedSecrets`
 * unconditionally — same posture as the desktop handler: this path has no
 * review-and-acknowledge UI, and the renderer's `sanitiseForClipboard` uses
 * the legacy pattern set (no Datadog / AWS secret-access-key / high-entropy
 * fallback), so a second-pass scrub closes that gap before anything leaves
 * the app.
 */
export function buildBugReportUrl(input) {
    const { text: body, count: secretFlagsRedacted } = redactFlaggedSecrets(composeBugReportBody(input.errorJson));
    const issueTitle = input.title ?? DEFAULT_TITLE;
    const { body: urlBody, truncated } = truncateForUrlEncoding(body, ENCODED_CAP);
    const url = ISSUE_URL_BASE +
        `?title=${encodeURIComponent(issueTitle)}` +
        `&body=${encodeURIComponent(urlBody)}` +
        '&labels=bug,from-foundry';
    return { url, body, truncated, secretFlagsRedacted };
}
//# sourceMappingURL=bug-report-url.js.map